import React, { useState, useEffect } from "react";
import {
  Form, Input, Select, DatePicker, Row, Col,
  Spin, message, Dropdown,
} from "antd";
import {
  IdcardOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../services/api";

const { Option } = Select;

// ── Helper FireDAC (padronizado) ────────────────────────────────────────
const extractRows = (data) => {
  if (!data) return [];
  if (data.FDBS) {
    try {
      const tables = data.FDBS.Manager.TableList;
      if (tables?.length > 0 && tables[0].RowList)
        return tables[0].RowList.map((r) => r.Original);
    } catch (e) {}
  }
  if (data.Table && Array.isArray(data.Table)) return data.Table;
  if (Array.isArray(data)) return data;
  return [];
};

export default function Passageiros({ clienteId: clienteIdProp }) {
  const [loading, setLoading]       = useState(false);
  const [passageiros, setPassageiros] = useState([]);
  const [filtro, setFiltro]         = useState("");
  const [clienteId, setClienteId]   = useState(null);
  const [modalOpen, setModalOpen]         = useState(false);
  const [saving, setSaving]               = useState(false);
  const [passageiroSelecionado, setPassSel] = useState(null);
  const [isMobile, setIsMobile]   = useState(window.innerWidth <= 768);
  const [form] = Form.useForm();

  // Detectar tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Resolve clienteId ──────────────────────────────────────────────────
  useEffect(() => {
    let id = clienteIdProp;
    if (!id) {
      const storedUser = localStorage.getItem("user");
      const storedUserId = localStorage.getItem("userID");
      if (storedUser) {
        try { const u = JSON.parse(storedUser); id = u.cli_id || u.id; } catch (e) {}
      }
      if (!id && storedUserId) id = parseInt(storedUserId);
    }
    if (id) { setClienteId(id); }
    else message.error("Usuário não autenticado.");
  }, [clienteIdProp]);

  useEffect(() => {
    if (clienteId) carregarPassageiros();
  }, [clienteId]);

  const carregarPassageiros = async () => {
    setLoading(true);
    try {
      const res = await api.get("/ServerPrincipal/PesquisaPassageiros", {
        params: { Cliente: clienteId },
      });
      setPassageiros(extractRows(res.data));
    } catch (e) {
      message.error("Erro ao carregar passageiros");
    } finally {
      setLoading(false);
    }
  };

  // ── Abre modal com dados preenchidos ─────────────────────────────────
  const abrirDetalhes = (p) => {
    setPassSel(p);
    const nomePartes = (p.pag_nome || "").split(" ");
    const nome      = nomePartes[0] || "";
    const sobrenome = nomePartes.slice(1).join(" ") || "";
    form.setFieldsValue({
      nome,
      sobrenome,
      email:         p.pag_email       || "",
      celular:       p.pag_telefone    || "",
      cpf:           p.pag_cpf         || "",
      passaporte:    p.pag_passaporte  || "",
      cnh:           p.pag_cnh         || "",
      rg:            p.pag_rg          || "",
      nacionalidade: p.pag_nascionalidade || "",
      sexo:          p.pag_sexo        || "NAO_DECLARADO",
      nascimento:    p.pag_data_nascimento ? dayjs(p.pag_data_nascimento) : null,
    });
    setModalOpen(true);
  };

  const abrirNovo = () => {
    setPassSel(null);
    form.resetFields();
    setModalOpen(true);
  };

  // ── Filtro de busca ───────────────────────────────────────────────────
  const passageirosFiltrados = passageiros.filter((p) => {
    const q = filtro.toLowerCase();
    return (
      (p.pag_nome  || "").toLowerCase().includes(q) ||
      (p.pag_email || "").toLowerCase().includes(q) ||
      (p.pag_cpf   || "").toLowerCase().includes(q)
    );
  });

  // ── Submit novo passageiro ────────────────────────────────────────────
  const handleSalvar = async (values) => {
    const temDoc = values.cpf || values.passaporte || values.cnh || values.rg;
    if (!temDoc) {
      message.error("Pelo menos um documento é obrigatório.");
      return;
    }
    if (!values.nacionalidade) {
      message.error("Nacionalidade é obrigatória.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/ServerPrincipal/InserirPassageiros", {
        IDCliente:      clienteId,
        Nome:           `${values.nome || ""} ${values.sobrenome || ""}`.trim(),
        CPF:            values.cpf          || "",
        RG:             values.rg           || "",
        Email:          values.email        || "",
        Telefone:       values.celular      || "",
        CNH:            values.cnh          || "",
        Nascionalidade: values.nacionalidade || "",
        Sexo:           values.sexo         || "NAO_DECLARADO",
        Nascimento:     values.nascimento   ? values.nascimento.format("DD/MM/YYYY") : "",
        Passaporte:     values.passaporte   || "",
      });
      message.success("Passageiro cadastrado com sucesso!");
      form.resetFields();
      setModalOpen(false);
      carregarPassageiros();
    } catch (e) {
      message.error(e.response?.data || "Erro ao cadastrar passageiro");
    } finally {
      setSaving(false);
    }
  };

  // ── Documento exibido na tabela ───────────────────────────────────────
  const docPrincipal = (p) =>
    p.pag_cpf || p.pag_passaporte || p.pag_cnh || p.pag_rg || "—";

  // ── Colunas visíveis no mobile ──────────────────────────────────────────
  const getVisibleColumns = () => {
    if (isMobile) {
      return ['nome', 'email', 'documento'];
    }
    return ['nome', 'email', 'contato', 'documento'];
  };

  const visibleCols = getVisibleColumns();

  const columnLabels = {
    nome: 'Nome',
    email: 'Email',
    contato: 'Contato',
    documento: 'Documento'
  };

  // ── Styles Responsivos ────────────────────────────────────────────
  const pageStyle = {
    minHeight: "100vh",
    background: "#f0f2f5",
    padding: isMobile ? "12px 8px" : "24px",
  };

  const cardStyle = {
    background: "#fff",
    borderRadius: 8,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    overflow: "hidden",
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    gap: isMobile ? 8 : 10,
    padding: isMobile ? "12px 16px" : "16px 20px",
    borderBottom: "1px solid #f0f0f0",
  };

  const filterBarStyle = {
    background: "#1a3a4a",
    padding: isMobile ? "12px 12px" : "16px 20px",
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: isMobile ? "stretch" : "flex-end",
    gap: isMobile ? 10 : 16,
  };

  const tableHeaderStyle = {
    display: "grid",
    gridTemplateColumns: isMobile ? "2fr 1.5fr 1.2fr" : "2fr 2fr 1.5fr 1.5fr 40px",
    padding: isMobile ? "8px 12px" : "10px 20px",
    background: "#fafafa",
    borderBottom: "1px solid #f0f0f0",
  };

  const thStyle = {
    fontSize: isMobile ? 10 : 12,
    color: "#888",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 4,
  };

  const rowStyle = (i) => ({
    display: "grid",
    gridTemplateColumns: isMobile ? "2fr 1.5fr 1.2fr" : "2fr 2fr 1.5fr 1.5fr 40px",
    padding: isMobile ? "10px 12px" : "12px 20px",
    borderBottom: "1px solid #f5f5f5",
    background: i % 2 === 0 ? "#fff" : "#fafafa",
    alignItems: "center",
  });

  const tdStyle = { 
    fontSize: isMobile ? 12 : 13, 
    color: "#333",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  // Modal styles
  const overlayStyle = {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 1000,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: isMobile ? "16px" : "0",
  };

  const modalStyle = {
    background: "#fff",
    borderRadius: 8,
    width: "100%",
    maxWidth: isMobile ? "100%" : 700,
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    overflow: "hidden",
    maxHeight: isMobile ? "90vh" : "auto",
  };

  const modalHeaderStyle = {
    display: "flex",
    alignItems: "center",
    gap: isMobile ? 8 : 10,
    padding: isMobile ? "12px 16px" : "16px 24px",
    borderBottom: "1px solid #f0f0f0",
  };

  const modalBodyStyle = {
    padding: isMobile ? "16px" : "24px",
    maxHeight: isMobile ? "calc(90vh - 200px)" : "70vh",
    overflowY: "auto",
  };

  const modalFooterStyle = {
    display: "flex",
    justifyContent: isMobile ? "center" : "flex-end",
    gap: isMobile ? 8 : 12,
    padding: isMobile ? "12px 16px" : "14px 24px",
    borderTop: "1px solid #f0f0f0",
    background: "#fafafa",
    flexDirection: isMobile ? "column" : "row",
  };

  const lbl = { 
    fontSize: isMobile ? 11 : 12, 
    color: "#555", 
    fontWeight: 500, 
    marginBottom: 4, 
    display: "block" 
  };

  const inp = {
    width: "100%",
    borderRadius: 4,
    border: "1px solid #d9d9d9",
    background: "#fff",
    height: isMobile ? 32 : 36,
    fontSize: isMobile ? 12 : 13,
    padding: "0 10px",
    outline: "none",
    boxSizing: "border-box",
  };

  const divider = {
    borderTop: "1px solid #f0f0f0",
    margin: isMobile ? "12px 0" : "16px 0",
  };

  const btnOrange = {
    height: isMobile ? 38 : 36,
    background: "#f5a623",
    border: "none",
    borderRadius: 4,
    color: "#fff",
    fontWeight: 700,
    fontSize: isMobile ? 12 : 13,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: isMobile ? "0 16px" : "0 20px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    width: isMobile ? "100%" : "auto",
  };

  const btnCancel = {
    height: isMobile ? 38 : 36,
    background: "#fff",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    color: "#555",
    fontSize: isMobile ? 12 : 13,
    padding: "0 16px",
    cursor: "pointer",
    width: isMobile ? "100%" : "auto",
  };

  const btnSave = {
    height: isMobile ? 38 : 36,
    background: saving ? "#bfbfbf" : "#1e3a4a",
    border: "none",
    borderRadius: 4,
    color: saving ? "#fff" : "#fff",
    fontSize: isMobile ? 12 : 13,
    fontWeight: 600,
    padding: "0 16px",
    cursor: saving ? "not-allowed" : "pointer",
    width: isMobile ? "100%" : "auto",
  };

  return (
    <div style={pageStyle}>
      <Spin spinning={loading}>
        <div style={cardStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <IdcardOutlined style={{ fontSize: isMobile ? 16 : 18, color: "#555" }} />
            <span style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, color: "#222" }}>Passageiros</span>
          </div>

          {/* Filter bar */}
          <div style={filterBarStyle}>
            <div style={{ flex: 1, width: isMobile ? "100%" : "auto" }}>
              <span style={{ fontSize: isMobile ? 10 : 11, color: "#aac", fontWeight: 500, display: "block", marginBottom: 4 }}>
                Filtrar por:
              </span>
              <div style={{ position: "relative" }}>
                <input
                  style={{ 
                    ...inp, 
                    paddingLeft: 12, 
                    height: isMobile ? 38 : 40, 
                    fontSize: isMobile ? 13 : 14,
                    width: "100%",
                  }}
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  placeholder="Nome, email ou CPF"
                />
              </div>
            </div>
            <button style={btnOrange} onClick={abrirNovo}>
              <span style={{ fontSize: isMobile ? 16 : 18, lineHeight: 1 }}>+</span>
              {isMobile ? "NOVO" : "NOVO PASSAGEIRO"}
            </button>
          </div>

          {/* Table header */}
          <div style={tableHeaderStyle}>
            {visibleCols.map((col) => (
              <div key={col} style={thStyle}>
                {columnLabels[col]}
              </div>
            ))}
            {!isMobile && <div />}
          </div>

          {/* Table rows */}
          {passageirosFiltrados.length === 0 ? (
            <div style={{ padding: isMobile ? "24px 16px" : "32px 20px", textAlign: "center", color: "#bbb", fontSize: isMobile ? 12 : 13 }}>
              Nenhum passageiro encontrado.
            </div>
          ) : (
            passageirosFiltrados.map((p, i) => (
              <div key={p.pag_id} style={rowStyle(i)}>
                <span style={{ ...tdStyle, fontWeight: 500 }}>
                  {isMobile ? (p.pag_nome || "—").slice(0, 20) + ((p.pag_nome || "").length > 20 ? '...' : '') : p.pag_nome || "—"}
                </span>
                <span style={{ ...tdStyle, color: "#555" }}>
                  {isMobile ? (p.pag_email || "—").slice(0, 20) + ((p.pag_email || "").length > 20 ? '...' : '') : p.pag_email || "—"}
                </span>
                {!isMobile && (
                  <span style={tdStyle}>{p.pag_telefone || "—"}</span>
                )}
                <span style={tdStyle}>
                  {isMobile ? docPrincipal(p).slice(0, 14) + (docPrincipal(p).length > 14 ? '...' : '') : docPrincipal(p)}
                </span>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Dropdown
                    trigger={["click"]}
                    placement="bottomRight"
                    menu={{
                      items: [
                        {
                          key: "detalhes",
                          label: (
                            <span style={{ fontSize: isMobile ? 12 : 13 }}>👤 Detalhes</span>
                          ),
                          onClick: () => abrirDetalhes(p),
                        },
                      ],
                    }}
                  >
                    <button
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "#aaa", fontSize: isMobile ? 18 : 20, 
                        padding: "0 6px", lineHeight: 1,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      ⋮
                    </button>
                  </Dropdown>
                </div>
              </div>
            ))
          )}
        </div>
      </Spin>

      {/* ── Modal Novo Passageiro ── */}
      {modalOpen && (
        <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}>
          <div style={modalStyle}>
            {/* Modal Header */}
            <div style={modalHeaderStyle}>
              <IdcardOutlined style={{ fontSize: isMobile ? 14 : 16, color: "#555" }} />
              {passageiroSelecionado ? (
                <span style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, color: "#222" }}>
                  Detalhes do Passageiro
                </span>
              ) : (
                <span style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, color: "#222" }}>
                  Novo Passageiro
                </span>
              )}
            </div>

            {/* Modal Body */}
            <div style={modalBodyStyle}>
              <Form form={form} layout="vertical" onFinish={handleSalvar}>

                {/* Nome + Sobrenome + Nascimento */}
                <Row gutter={[isMobile ? 8 : 12, 8]}>
                  <Col xs={24} sm={8}>
                    <Form.Item name="nome" label="Nome" style={{ marginBottom: 0 }}>
                      <Input size={isMobile ? "small" : "middle"} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="sobrenome" label="Sobrenome" style={{ marginBottom: 0 }}>
                      <Input size={isMobile ? "small" : "middle"} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="nascimento" label="Nascimento" style={{ marginBottom: 0 }}>
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                        size={isMobile ? "small" : "middle"}
                        placeholder=""
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Email + Celular */}
                <Row gutter={[isMobile ? 8 : 12, 8]} style={{ marginTop: isMobile ? 8 : 14 }}>
                  <Col xs={24} sm={14}>
                    <Form.Item name="email" label="Email" style={{ marginBottom: 0 }}>
                      <Input
                        size={isMobile ? "small" : "middle"}
                        placeholder="Ex. fulano@example.org"
                        suffix={<span style={{ color: "#bbb" }}>✉</span>}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={10}>
                    <Form.Item name="celular" label="Celular" style={{ marginBottom: 0 }}>
                      <Input
                        size={isMobile ? "small" : "middle"}
                        placeholder="(XX) XXXXX-XXXXX"
                        suffix={<span style={{ color: "#bbb" }}>📞</span>}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <div style={divider} />

                {/* CPF + Passaporte + CNH + RG */}
                <Row gutter={[isMobile ? 8 : 12, 8]}>
                  <Col xs={12} sm={6}>
                    <Form.Item name="cpf" label="CPF" style={{ marginBottom: 0 }}>
                      <Input 
                        size={isMobile ? "small" : "middle"} 
                        placeholder="XXX.XXX-XX" 
                        suffix={<span style={{ color: "#bbb" }}>#</span>} 
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item name="passaporte" label="Passaporte" style={{ marginBottom: 0 }}>
                      <Input 
                        size={isMobile ? "small" : "middle"} 
                        placeholder="XXXXXXXX" 
                        suffix={<span style={{ color: "#bbb" }}>#</span>} 
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item name="cnh" label="CNH" style={{ marginBottom: 0 }}>
                      <Input 
                        size={isMobile ? "small" : "middle"} 
                        placeholder="Habilitação" 
                        suffix={<span style={{ color: "#bbb" }}>#</span>} 
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item name="rg" label="RG" style={{ marginBottom: 0 }}>
                      <Input 
                        size={isMobile ? "small" : "middle"} 
                        placeholder="Registro geral" 
                        suffix={<span style={{ color: "#bbb" }}>#</span>} 
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <div style={{ marginTop: isMobile ? 4 : 8 }}>
                  <span style={{ fontSize: isMobile ? 10 : 11, color: "#cf1322" }}>
                    Pelo menos um documento é obrigatório.
                  </span>
                </div>

                <div style={divider} />

                {/* Nacionalidade + Sexo */}
                <Row gutter={[isMobile ? 8 : 12, 8]}>
                  <Col xs={24} sm={14}>
                    <Form.Item
                      name="nacionalidade"
                      label="Nacionalidade"
                      rules={[{ required: true, message: "Campo obrigatório." }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        size={isMobile ? "small" : "middle"}
                        placeholder="Digite a nacionalidade"
                        suffix={<span style={{ color: "#bbb" }}>🔍</span>}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={10}>
                    <Form.Item name="sexo" label="Sexo" style={{ marginBottom: 0 }} initialValue="NAO_DECLARADO">
                      <Select size={isMobile ? "small" : "middle"} style={{ width: "100%" }}>
                        <Option value="NAO_DECLARADO">Não declarado</Option>
                        <Option value="MASCULINO">Masculino</Option>
                        <Option value="FEMININO">Feminino</Option>
                        <Option value="OUTRO">Outro</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

              </Form>
            </div>

            {/* Modal Footer */}
            <div style={modalFooterStyle}>
              <button
                style={btnCancel}
                onClick={() => { form.resetFields(); setModalOpen(false); }}
              >
                CANCELAR
              </button>
              <button
                style={btnSave}
                onClick={() => form.submit()}
                disabled={saving}
              >
                {saving ? "SALVANDO..." : "CADASTRAR"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}